import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashGet from 'lodash/fp/get';
import lodashPick from 'lodash/fp/pick';
import lodashSortBy from 'lodash/fp/sortBy';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  LISTING_STATUS_ACTIVE,
} from 'common/listingConstants';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrLink from 'client/components/SrLink/SrLink';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFormikField, {
  SR_FIELD_THEME_ADMIN,
} from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';

const apartmentFormFieldIds = [
  'selfTourFlowEnabled',
  'streetEntrancePhotoUrls',
  'hasGateCode',
  'gatePhotoUrls',
  'gateCode',
  'parkingPhotoUrls',
  'parkingInstructions',
];

const aparmentBuildingFormFieldIds = [
  'buildingNumber',
  'hasAccessCode',
  'accessPhotoUrls',
  'accessCode',
  'accessInstructions',
];

const listingFormFieldIds = [
  'walkToUnitInstructions',
  'walkToUnitPhotoUrls',
];

const makeApartmentBuildingFormFieldPathGetter = (apartmentBuilding) => {
  const apartmentBuildingPath = `apartmentBuildings-${apartmentBuilding.id}`;
  return (formFieldId) => {
    return `${apartmentBuildingPath}-${formFieldId}`;
  };
};

const makeListingFormFieldPathGetter = (listing) => {
  const listingFieldPath = `listings-${listing.id}`;
  return (formFieldId) => {
    return `${listingFieldPath}-${formFieldId}`;
  };
};

const getFormFields = ({
  apartmentBuildings,
  listings,
  formValues,
}) => {
  const formFields = [
    {
      id: 'selfTourFlowEnabled',
      title: 'self tour flow enabled',
      type: 'yes_no',
    },
    {
      id: 'streetEntrancePhotoUrls',
      title: 'street entrance photos',
      type: 'file_multi',
      properties: {
        filePath: '/apartment-uploads/street-entrance-photos/{uuid}',
        fileAccept: 'image/*',
      },
      validations: formValues.selfTourFlowEnabled
        ? ['nonEmptyArray']
        : [],
    },
    {
      id: 'hasGateCode',
      title: 'has gate code',
      type: 'yes_no',
      validations: formValues.selfTourFlowEnabled
        ? ['required']
        : [],
    },
    hideFieldIf(!formValues.hasGateCode, {
      id: 'gatePhotoUrls',
      title: 'gate code photos',
      type: 'file_multi',
      properties: {
        filePath: '/apartment-uploads/gate-code-photos/{uuid}',
        fileAccept: 'image/*',
      },
      validations: ['required'],
    }),
    hideFieldIf(!formValues.hasGateCode, {
      id: 'gateCode',
      title: 'gate code',
      type: 'short_text',
      validations: ['required'],
    }),
    {
      id: 'parkingPhotoUrls',
      title: 'parking photos',
      type: 'file_multi',
      properties: {
        filePath: '/apartment-uploads/parking-photos/{uuid}',
        fileAccept: 'image/*',
      },
      validations: formValues.selfTourFlowEnabled
        ? ['nonEmptyArray']
        : [],
    },
    {
      id: 'parkingInstructions',
      title: 'parking instructions',
      type: 'long_text',
      properties: {
        placeholder: '',
      },
      validations: formValues.selfTourFlowEnabled
        ? ['required']
        : [],
    },
  ];

  apartmentBuildings.forEach((apartmentBuilding) => {
    const getApartmentBuildingFormField = makeApartmentBuildingFormFieldPathGetter(apartmentBuilding);
    const hasAccessCode = formValues[getApartmentBuildingFormField('hasAccessCode')];

    formFields.push(...[
      {
        id: getApartmentBuildingFormField('buildingNumber'),
        title: 'building number',
        type: 'number',
        validations: formValues.selfTourFlowEnabled
          ? ['required']
          : [],
      },
      {
        id: getApartmentBuildingFormField('hasAccessCode'),
        title: 'has access code',
        type: 'yes_no',
        validations: formValues.selfTourFlowEnabled
          ? ['required']
          : [],
      },
      hideFieldIf(!hasAccessCode, {
        id: getApartmentBuildingFormField('accessPhotoUrls'),
        title: 'access photos',
        type: 'file_multi',
        properties: {
          filePath: '/apartment-building-uploads/access-photos/{uuid}',
          fileAccept: 'image/*',
        },
        validations: ['nonEmptyArray'],
      }),
      hideFieldIf(!hasAccessCode, {
        id: getApartmentBuildingFormField('accessCode'),
        title: 'access code',
        type: 'short_text',
        validations: ['required'],
      }),
      hideFieldIf(!hasAccessCode, {
        id: getApartmentBuildingFormField('accessInstructions'),
        title: 'access instructions',
        type: 'long_text',
        validations: ['required'],
      }),
    ]);
  });

  listings.forEach((listing) => {
    const getListingFormFieldPath = makeListingFormFieldPathGetter(listing);
    const isListingActive = listing.status === LISTING_STATUS_ACTIVE;

    formFields.push(...[
      {
        id: getListingFormFieldPath('walkToUnitPhotoUrls'),
        title: 'walk to unit photos',
        type: 'file_multi',
        properties: {
          filePath: '/listing-uploads/walk-to-unit-photos/{uuid}',
          fileAccept: 'image/*',
        },
        validations: formValues.selfTourFlowEnabled && isListingActive
          ? ['nonEmptyArray']
          : [],
      },
      {
        id: getListingFormFieldPath('walkToUnitInstructions'),
        title: 'walk to unit instructions',
        type: 'long_text',
        validations: formValues.selfTourFlowEnabled && isListingActive
          ? ['required']
          : [],
      },
    ]);
  });

  return formFields;
};

const SrAdminApartmentSelfTourMultiFamilyModal = ({
  apartment,
  apartmentBuildings,
  listings: allListings,
  onClose,
  onChange,
  dispatch,
}) => {
  const listings = lodashSortBy('status', allListings);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isSubmittedForm, setIsSubmittedForm] = useState(false);

  const onValidateForm = (formikBag) => {
    const formValues = formikBag.values;
    const formFields = getFormFields({
      apartmentBuildings,
      listings,
      formValues,
    });

    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFielIdsWithErrors = Object.keys(formErrors);

    if (!formFielIdsWithErrors.length) {
      formikBag.handleSubmit();
    }
    else {
      formFielIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFielIdsWithErrors[0],
      });
    }
  };

  const onSubmitForm = (formValues) => {
    setIsSubmittingForm(true);

    const calls = [
      dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        payload: {
          table: 'apartments',
          where: {
            id: apartment.id,
          },
          updates: setNullEmptyValues(lodashPick(apartmentFormFieldIds, formValues)),
        },
      }),
    ];

    apartmentBuildings.forEach((apartmentBuilding) => {
      const apartmentBuildingValues = aparmentBuildingFormFieldIds.reduce((memo, formFieldId) => {
        memo[formFieldId] = lodashGet(
          `apartmentBuildings-${apartmentBuilding.id}-${formFieldId}`, formValues
        );
        return memo;
      }, {});
      calls.push(dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        queryId: String(apartmentBuilding.id),
        payload: {
          table: 'apartmentBuildings',
          where: {
            id: apartmentBuilding.id,
          },
          updates: setNullEmptyValues(apartmentBuildingValues),
        },
      }));
    });

    listings.forEach((listing) => {
      const getListingFormFieldPath = makeListingFormFieldPathGetter(listing);
      const listingFormValues = listingFormFieldIds.reduce((memo, formFieldId) => {
        memo[formFieldId] = lodashGet(getListingFormFieldPath(formFieldId), formValues);
        return memo;
      }, {});

      calls.push(dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        queryId: String(listing.id),
        payload: {
          table: 'listings',
          where: {
            id: listing.id,
          },
          updates: setNullEmptyValues(listingFormValues),
        },
      }));
    });

    Promise.all(calls).then(() => {
      setIsSubmittedForm(true);
      setTimeout(() => {
        onClose();
        onChange();
      }, 300);
    });
  };

  const apartmentBuildingValues = apartmentBuildings.reduce((memo, apartmentBuilding) => {
    aparmentBuildingFormFieldIds.forEach((formFieldId) => {
      memo[`apartmentBuildings-${apartmentBuilding.id}-${formFieldId}`] = apartmentBuilding[formFieldId];
    });
    return memo;
  }, {});
  const listingFormValues = listings.reduce((memo, listing) => {
    const getListingFormFieldPath = makeListingFormFieldPathGetter(listing);
    listingFormFieldIds.forEach((formFieldId) => {
      memo[getListingFormFieldPath(formFieldId)] = listing[formFieldId];
    });
    return memo;
  }, {});
  const initialFormValues = {
    ...lodashPick(apartmentFormFieldIds, apartment),
    ...apartmentBuildingValues,
    ...listingFormValues,
  };

  const listingsByApartmentBuildingId = listings.reduce((memo, listing) => {
    return {
      ...memo,
      [listing.apartmentBuildingId]: [
        ...(memo[listing.apartmentBuildingId] || []),
        listing,
      ],
    };
  }, {});

  return (
    <SrDarkModal className='SrAdminApartmentSelfTourMultiFamilyModal'>
      <SrBox className='closeIcon' onClick={onClose}>
        <SrIcon className='x-close-large-white' />
      </SrBox>
      <SrModalContent>
        <Formik
          initialValues={createInitialValues({
            formFields: getFormFields({
              apartmentBuildings,
              listings,
              formValues: initialFormValues,
            }),
            defaultValues: initialFormValues,
          })}
          validate={(formValues) => {
            const formFields = getFormFields({
              apartmentBuildings,
              listings,
              formValues,
            });
            return createFormValidationFn(formFields)(formValues);
          }}
          onSubmit={onSubmitForm}
          render={(formikBag) => {
            const formFields = getFormFields({
              apartmentBuildings,
              listings,
              formValues: formikBag.values,
            });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            const _onValidateForm = () => {
              onValidateForm(formikBag);
            };

            return (
              <Form>
                <SrBox>
                  <SrText font='whitney-sc' weight='bold' size='31'>apartment</SrText>
                  <SrFlex wrap='wrap' direction='col'>
                    {getFormFieldsById(apartmentFormFieldIds).map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        className={SR_FIELD_THEME_ADMIN}
                      />
                    ))}
                  </SrFlex>
                </SrBox>
                <SrBox margin='40px 0'>
                  {apartmentBuildings.map((apartmentBuilding) => {
                    return (
                      <SrBox key={`apartmentBuildings-${apartmentBuilding.id}`}>
                        <SrText font='whitney-sc' weight='bold' size='31'>
                          building - {apartmentBuilding.addressStreetDisplay}
                        </SrText>
                        <SrFlex wrap='wrap' direction='col'>
                          {getFormFieldsById(aparmentBuildingFormFieldIds.map((formFieldId) => (
                            `apartmentBuildings-${apartmentBuilding.id}-${formFieldId}`
                          ))).map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              formField={formField}
                              formikBag={formikBag}
                              className={SR_FIELD_THEME_ADMIN}
                            />
                          ))}
                        </SrFlex>
                        {listingsByApartmentBuildingId[apartmentBuilding.id] && (
                          <SrBox marginTop='10px'>
                            {listingsByApartmentBuildingId[apartmentBuilding.id].map((listing) => {
                              const getListingFormFieldPath = makeListingFormFieldPathGetter(listing);
                              return (
                                <SrBox key={listing.id} marginTop='5px'>
                                  <SrLink
                                    to={`/admin/listings/edit/${listing.id}`}
                                    target='_blank'
                                    className='editListingLink'
                                  >
                                    <SrText font='whitney-sc' weight='bold' size='31'>
                                      unit {listing.apartmentUnit} - {listing.status.toLowerCase()}
                                    </SrText>
                                  </SrLink>
                                  <SrFlex direction='col'>
                                    {getFormFieldsById(listingFormFieldIds.map(getListingFormFieldPath))
                                      .map((formField) => (
                                        <SrFormikField
                                          key={formField.id}
                                          formField={formField}
                                          formikBag={formikBag}
                                          className={SR_FIELD_THEME_ADMIN}
                                        />
                                      ))
                                    }
                                  </SrFlex>
                                </SrBox>
                              );
                            })}
                          </SrBox>
                        )}
                      </SrBox>
                    );
                  })}
                </SrBox>
                <SrBox className='submitButtonContainer'>
                  <ButtonDeprecated
                    onClick={_onValidateForm}
                    disabled={isSubmittedForm || isSubmittingForm}
                  >
                    {isSubmittedForm ? 'saved' : 'submit'}
                  </ButtonDeprecated>
                </SrBox>
              </Form>
            );
          }}
        />
      </SrModalContent>
    </SrDarkModal>
  );
};

SrAdminApartmentSelfTourMultiFamilyModal.propTypes = {
  apartment: PropTypes.object.isRequired,
  apartmentBuildings: PropTypes.array.isRequired,
  listings: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrAdminApartmentSelfTourMultiFamilyModal.displayName = 'SrAdminApartmentSelfTourMultiFamilyModal';

export default SrAdminApartmentSelfTourMultiFamilyModal;
